<template>
  <!-- backdrop-blur flex-none transition-colors duration-500 lg:z-50 lg:border-b lg:border-slate-900/10 supports-backdrop-blur:bg-white/60 -->
  <div :class="[isFullScreen ? 'min-h-screen' : 'h-full resizable-wrapper', isPrintLayout ? '' : 'dark bg-slate-800/95 text-slate-300', 'text-lg font-sans']">
    <div v-if="!isFullScreen" class="resize-handle" @mousedown="startResizing"></div>
    <div :class="[isFullScreen ? 'w-full' : 'resizable-div']" :style="isFullScreen ? {} : { width: `${width}px` }">
      <div :class="['w-full border-t border-slate-600', isFullScreen ? '' : 'absolute top-0']">
        <div class="flex justify-between items-center mr-10 backdrop-blur">
          <div class="flex items-center ml-2">
            <button
              v-tooltip.bottom="'Hide Data Assistant'"
              @click="dataAssistantStore.visible = !dataAssistantStore.visible"
              :class="[
                'p-1 mr-4 rounded border border-transparent shadow-gradient-glow-green',
                dataAssistantStore.visible ? 'text-slate-100 bg-emerald-600/70 hover:bg-emerald-500/70' : 'text-slate-600 hover:bg-slate-100/70',
              ]"
            >
              <mdicon name="creation" size="18" aria-hidden="true"></mdicon>
            </button>

            <ModelSelector :title="selectedModel" :items="availableModels" @select="onModelSelect" class="" />
          </div>
          <div class="flex items-center">
            <button
              v-tooltip.bottom="'Hide Data Assistant'"
              @click="onClose"
              class="p-1 text-slate-300 hover:text-slate-100 rounded shadow-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-700/60"
            >
              <mdicon name="close" size="22" aria-hidden="true"></mdicon>
            </button>
            <button v-tooltip.bottom="'Print'" @click="onPrint" class="p-1 text-slate-300 hover:text-slate-100 rounded shadow-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-700/60">
              <mdicon name="printer" size="22" aria-hidden="true"></mdicon>
            </button>
            <button
              v-tooltip.bottom="'Toggle Full Screen'"
              @click="dataAssistantStore.fullScreen = !dataAssistantStore.fullScreen"
              class="p-1 text-slate-300 hover:text-slate-100 rounded shadow-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-700/60"
            >
              <mdicon :name="dataAssistantStore.fullScreen ? 'fullscreen-exit' : 'fullscreen'" size="22" aria-hidden="true"></mdicon>
            </button>
            <button
              v-if="hasMessages"
              v-tooltip.bottom="'Clear conversation'"
              @click="onClearConversation"
              class="p-1 text-slate-300 hover:text-slate-100 mr-2 rounded shadow-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-700/60"
            >
              <mdicon name="autorenew" size="22" aria-hidden="true"></mdicon>
            </button>
          </div>
        </div>
      </div>
      <div v-if="hasMessages" class="z-10 h-full">
        <div :class="[isFullScreen ? '' : 'grid grid-rows-[1fr_auto] h-full overflow-hidden']">
          <!-- Top Content -->
          <div class="overflow-y-auto custom-scrollbar custom-scrollbar-dark mx-4">
            <div :class="[isFullScreen ? 'max-w-4xl' : 'max-w-2xl', 'flex flex-col space-y-6 pt-12 pb-2 px-2 mx-auto']">
              <div v-if="hasLabel" class="text-slate-400 text-sm p-4 border border-slate-500 rounded-lg max-w-4xl mb-10">
                {{ dataAssistantStore.dataLabel }}
              </div>
              <div v-for="(message, index) in conversationMessages" :key="index" :class="['flex items-start', message.role === 'user' ? 'self-end max-w-2xl' : '']">
                <mdicon :name="message.role === 'user' ? 'account' : 'alpha-c-box-outline'" size="28" class="text-slate-500 mr-2" aria-hidden="true"></mdicon>
                <div v-if="message.content && message.content !== ''" :class="[message.role === 'user' ? 'rounded-lg px-4 py-1 bg-slate-700 text-white self-end max-w-2xl' : 'text-slate-400']">
                  <div class="text-xs text-slate-400">{{ message.role }}</div>
                  <div v-if="message.role === 'assistant'" class="prose dark:prose-invert font-source text-md rounded-md px-1 -ml-1" v-mermaid v-html="getMessageHtml(message)"></div>
                  <div v-else class="prose dark:prose-invert font-source text-md rounded-md px-1 -ml-1">{{ message.content }}</div>
                  <ClipboardCopyButton v-if="message.role === 'assistant'" class="-ml-2" v-tooltip.top="'Copy answer'" @click="onAnswerCopy(message)"></ClipboardCopyButton>
                </div>
              </div>
              <div v-if="dataAssistantStore.thinking" :class="['flex items-start']">
                <mdicon name="alpha-c-box-outline" size="28" class="text-slate-500 mr-2" aria-hidden="true"></mdicon>
                <div :class="['text-slate-400']">
                  <div class="text-xs text-slate-400">assistant</div>
                  <Skeleton width="20rem" class="my-2"></Skeleton>
                  <Skeleton width="15rem" class="mb-2"></Skeleton>
                </div>
              </div>
            </div>
            <div ref="messageListBottomAnchor" :class="[isFullScreen ? 'h-[120px]' : 'h-1']"></div>
          </div>
          <!-- Footer -->
          <div :class="[isFullScreen ? 'fixed bottom-10 left-1/2 -translate-x-1/2 w-1/2' : 'mb-4', 'print:hidden']">
            <DAUserInput @user-input="onUserInput" :showLabel="false" :class="[isFullScreen ? '' : 'max-w-4xl mx-auto px-4']" />
          </div>
        </div>
      </div>
      <div v-else class="h-full flex flex-col items-center justify-center">
        <div v-if="hasLabel" class="text-slate-400 text-sm p-4 border border-slate-500 rounded-lg max-w-4xl mb-10">
          {{ dataAssistantStore.dataLabel }}
        </div>
        <DAUserInput
          @user-input="onUserInput"
          :label="dataAssistantStore.inputLabel"
          :placeholder="dataAssistantStore.inputPlaceholder"
          :description="dataAssistantStore.inputDescription"
          class="w-full max-w-4xl mx-auto px-2"
        />
        <div v-if="hasSuggestedQuestions" class="mt-6">
          <div v-for="(q, idx) in dataAssistantStore.suggestedQuestions" :key="idx" class="mt-4">
            <button @click="onUserInput(q)" class="flex items-center text-slate-300 rounded-md py-2 px-2 border border-slate-500 text-sm hover:text-slate-50 hover:bg-slate-700">
              <mdicon name="help-box-multiple-outline" size="20" class="text-slate-400 mr-2" aria-hidden="true"></mdicon>
              {{ q }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, nextTick } from 'vue';
  import { useRoute } from 'vue-router';
  import { useMd } from '@/hooks/useMd.js';
  import { useDataAssistantStore } from '@/store/DataAssistantStore';
  import DAUserInput from '@/components/DataAssistant/DAUserInput.vue';
  import Skeleton from 'primevue/skeleton';
  import DropdownSelectButton from '@/components/Buttons/DropdownSelectButton.vue';
  import Dropdown from 'primevue/dropdown';
  import ModelSelector from '@/components/Selectors/ModelSelector.vue';
  import ClipboardCopyButton from '@/components/Buttons/ClipboardCopyButton.vue';
  import Button from 'primevue/button';

  const tmpdata = 'graph TD\n' + '    A[Christmas] -->|Get money| B(Go shopping)\n' + '    B --> C{Let me think}\n' + '    C -->|One| D[Laptop]\n' + '    C -->|Two| E[iPhone]\n' + '    C -->|Three| F[Car]\n';
  const tmpmsgs = ref([]);

  const props = defineProps({
    someFlag: {
      type: Boolean,
      default: false,
    },
  });

  const route = useRoute();
  const md = useMd();
  const dataAssistantStore = useDataAssistantStore();

  let prePrintFullScreen = null;

  const availableModels = [
    { name: 'o4-mini', title: 'o4-mini', description: 'o4-mini', icon: '' },
    { name: 'o3', title: 'o3', description: 'o3', icon: '' },
    { name: 'gemini-2.5-flash', title: 'Gemini 2.5 Flash', description: 'Gemini 2.5 Flash', icon: '' },
    //{ name: 'gemini-2.5-pro', title: 'Gemini 2.5 Pro', description: 'Gemini 2.5 Pro', icon: '' },
    //{ name: 'o3', title: 'o3', description: 'o3', icon: '' },
    //{ name: 'o3-mini', title: 'o3-mini', description: 'o3-mini', icon: '' },
    { name: 'gpt-4.1', title: 'gpt-4.1', description: 'gpt-4.1', icon: '' },
    { name: 'deepseek-r1-distill-llama-70b', title: 'DeepSeek', description: 'DeepSeek', icon: '' },
  ];
  const selectedModel = computed(() => availableModels.find((m) => m.name === dataAssistantStore.modelName)?.title || dataAssistantStore.modelName);

  const isResizing = ref(false);
  const startX = ref(0);
  const startWidth = ref(dataAssistantStore.width); // todo double-check how this coexists with the store
  const width = ref(startWidth.value);

  const messageListBottomAnchor = ref(null);
  let scrollTimeout = null;

  const hasMessages = computed(() => dataAssistantStore.messages.length > 0);
  const hasLabel = computed(() => dataAssistantStore.dataLabel && dataAssistantStore.dataLabel !== '');
  const hasSuggestedQuestions = computed(() => dataAssistantStore.suggestedQuestions.length > 0);
  const conversationMessages = computed(() => dataAssistantStore.messages);

  const isVisible = computed(() => dataAssistantStore.visible);
  const isFullScreen = computed(() => dataAssistantStore.fullScreen);
  const isPrintLayout = computed(() => dataAssistantStore.printLayout);

  function getMessageClass(message) {
    return message.role === 'assistant' ? 'bg-slate-50 text-slate-800' : 'bg-slate-900 text-slate-50';
  }

  function getMessageHtml(message) {
    return md.mdToHtml(message.content);
  }

  function scrollToBottom() {
    if (messageListBottomAnchor.value) {
      messageListBottomAnchor.value.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }

  watch(
    () => dataAssistantStore.thinking,
    (val) => {
      if (val) {
        console.log(`Must scroll - thinking!`);
        setTimeout(() => {
          scrollToBottom();
        }, 500);
      }
    },
  );

  // Hide the assistant when the route changes
  watch(
    () => route.path,
    async (newId) => {
      if (isVisible) {
        console.log('DA: Route changed - hiding assistant');
        dataAssistantStore.visible = false;
        dataAssistantStore.fullScreen = false;
      }
    },
  );

  function startResizing(event) {
    isResizing.value = true;
    startX.value = event.clientX;
    startWidth.value = width.value;
    // Add mousemove and mouseup event listeners
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResizing);
  }

  function resize(event) {
    if (isResizing.value) {
      // Calculate new width based on mouse movement
      const w = startWidth.value - (event.clientX - startX.value);
      if (w > 400) {
        width.value = w;
        dataAssistantStore.setWidth(w);
      }
    }
  }

  function stopResizing() {
    isResizing.value = false;
    // Remove event listeners when resizing is done
    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResizing);
  }

  function onModelSelect(modelInfo) {
    dataAssistantStore.setModel(modelInfo.name);
  }

  function onClearConversation() {
    dataAssistantStore.clearConversation();
  }

  function onAnswerCopy(message) {
    navigator.clipboard.writeText(message.content);
  }

  async function onUserInput(userInput) {
    await dataAssistantStore.ask(userInput);
    scrollToBottom();
  }

  function onClose() {
    dataAssistantStore.fullScreen = false;
    dataAssistantStore.visible = false;
  }

  async function onPrint() {
    prePrintFullScreen = dataAssistantStore.fullScreen;
    dataAssistantStore.fullScreen = true;
    // TODO this needs tuning up - dark/light mode
    //dataAssistantStore.printLayout = true;
    await nextTick();
    window.addEventListener('afterprint', handlePrintClose);
    window.print();
  }

  function handlePrintClose() {
    dataAssistantStore.fullScreen = prePrintFullScreen;
    //dataAssistantStore.printLayout = false;
    window.removeEventListener('afterprint', handlePrintClose);
  }
</script>
<style scoped>
  .resizable-wrapper {
    display: flex;
    align-items: stretch;
  }

  .resizable-div {
    min-width: 100px; /* Minimum width */
    max-width: 100%; /* Prevent overflow */
  }

  .resize-handle {
    width: 6px; /* Handle width */
    cursor: ew-resize;
    position: relative;
    background-color: #475569;
  }

  .resize-handle:hover {
    background-color: #94a3b8;
  }

  .resize-handle::before {
    content: '';
    position: absolute;
    top: 48%; /* Adjust as needed */
    bottom: 48%; /* Adjust as needed */
    left: 40%;
    width: 1px; /* Width of the lines */
    background-color: #e2e8f0; /* Line color */
    transform: translateX(-50%);
    box-shadow: 2px 0 0 0 #e2e8f0;
    /* Three lines:
    box-shadow:
      -2px 0 0 0 #888,
      2px 0 0 0 #888;
    */
  }
  .p-skeleton {
    background-color: #475569;
  }

  .p-skeleton:after {
    background: linear-gradient(90deg, rgba(148, 163, 184, 0), rgba(148, 163, 184, 0.4), rgba(71, 85, 105, 0.5));
  }
</style>
